export const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      features: "Features",
      about: "About",
      contact: "Contact"
    },

    // Home Page
    home: {
      title: "Your Digital Dreams, Made Real",
      heroTitle: "From Idea to Impact, Stronger Together",
      subtitle: "At Agnus Cloud we build digital solutions that strengthen your mission. Through trust, craftsmanship and vision.",
      exploreTools: "See What We Do",
      learnMore: "Get to Know Us",
      whyChoose: "Why Choose Agnus Cloud?",
      whyChooseSubtitle: "We're not just another tech company. We're the team that gets excited about your success and treats your project like it's our own.",
      trustedBy: "Trusted by Amazing Businesses",
      trustedBySubtitle: "Real people, real results, real relationships",
      readyTransform: "Ready to Make Something Awesome?",
      readyTransformSubtitle: "Let's grab a coffee (virtual or real) and chat about your dreams. We love turning 'what if' into 'wow, that's incredible!'",
      startProject: "Let's Build Together",
      stats: {
        projects: "Projects Delivered",
        uptime: "Uptime Guarantee",
        support: "Support Available",
        clients: "Happy Clients"
      },
      features: {
        cloudNative: {
          title: "Cloud-Native Solutions",
          description: "Scalable, modern applications built for the cloud-first world."
        },
        lightningFast: {
          title: "Lightning Fast",
          description: "Optimized performance that delivers results at the speed of thought."
        },
        enterpriseSecurity: {
          title: "Enterprise Security",
          description: "Bank-grade security protocols protecting your most valuable data."
        },
        creativeInnovation: {
          title: "Creative Innovation",
          description: "Cutting-edge technology meets creative problem-solving."
        }
      }
    },

    // Partners Section
    partners: {
      title: "The Amazing Tools We Work With",
      subtitle: "We've partnered with the best companies in tech to bring you solutions that just work. Every tool we choose helps us build something incredible for your business.",
      techStack: "Our Technology Toolkit",
      techStackDescription: "We've carefully chosen each tool in our toolkit because they help us build something amazing for you. Whether it's keeping your code safe with GitHub, making your site lightning-fast with Vercel, or ensuring everything runs smoothly with UptimeRobot, every choice we make is about delivering the best experience for your business.",
      details: {
        github: {
          title: "GitHub",
          description: "Version Control & Collaboration",
          integration: "We use GitHub to manage our codebase, collaborate efficiently, and ensure version control for all our projects. It enables seamless teamwork and code reviews, keeping our development process transparent and reliable."
        },
        nextjs: {
          title: "Next.js",
          description: "React Framework",
          integration: "Next.js powers our frontend with server-side rendering, static site generation, and API routes. It allows us to build fast, SEO-friendly, and scalable web applications for our clients."
        },
        vercel: {
          title: "Vercel",
          description: "Deployment Platform",
          integration: "We deploy our applications on Vercel for instant, global delivery. Vercel's edge network ensures our sites are fast and reliable, with zero-config deployments and seamless integration with Next.js."
        },
        cloudflare: {
          title: "Cloudflare",
          description: "CDN & Security",
          integration: "Cloudflare protects our sites from threats and accelerates content delivery worldwide. We use it for DDoS protection, SSL, and caching, ensuring security and performance for all users."
        },
        uptimerobot: {
          title: "UptimeRobot",
          description: "Monitoring & Alerts",
          integration: "UptimeRobot monitors our services 24/7, alerting us instantly if anything goes down. This helps us maintain high availability and respond quickly to any issues."
        },
        supabase: {
          title: "Supabase",
          description: "Backend as a Service",
          integration: "Supabase provides our backend infrastructure, including authentication, database, and real-time APIs. It allows us to build secure, scalable apps without managing servers."
        },
        mijndomein: {
          title: "MijnDomein.nl",
          description: "Domain Management",
          integration: "We use MijnDomein.nl to manage our domains, DNS, and SSL certificates. It ensures our web properties are always accessible and secure."
        }
      }
    },

    // Features Page
    features: {
      title: "Our Features",
      subtitle: "Discover the comprehensive suite of services and technologies that make Agnus Cloud the perfect partner for your digital transformation journey.",
      serviceCategoriesTitle: "Service Categories",
      serviceCategoriesSubtitle: "Explore our comprehensive range of services designed to meet all your technology needs.",
      faq: "Frequently Asked Questions",
      faqSubtitle: "Get answers to common questions about our services and processes.",
      readyToStart: "Ready to Get Started?",
      readyToStartSubtitle: "Let's discuss how our features can help transform your business. Contact us today for a free consultation.",
      getStartedToday: "Get Started Today",
      learnAboutUs: "Learn About Us",
      mainFeatures: {
        cloudInfrastructure: {
          title: "Cloud Infrastructure",
          description: "Scalable, reliable cloud solutions built for modern businesses.",
          features: ["Auto-scaling", "99.9% uptime", "Global CDN", "Disaster recovery"]
        },
        customDevelopment: {
          title: "Custom Development",
          description: "Tailored software solutions that fit your unique business needs.",
          features: ["Full-stack development", "API integration", "Legacy modernization", "Code optimization"]
        },
        mobileSolutions: {
          title: "Mobile Solutions",
          description: "Native and cross-platform mobile apps that engage users.",
          features: ["iOS & Android", "React Native", "Progressive Web Apps", "App Store optimization"]
        },
        dataAnalytics: {
          title: "Data Analytics",
          description: "Transform your data into actionable business insights.",
          features: ["Real-time dashboards", "Predictive analytics", "Data visualization", "Machine learning"]
        },
        cybersecurity: {
          title: "Cybersecurity",
          description: "Comprehensive security solutions to protect your digital assets.",
          features: ["Threat detection", "Compliance auditing", "Security training", "Incident response"]
        },
        digitalTransformation: {
          title: "Digital Transformation",
          description: "End-to-end digital transformation for modern enterprises.",
          features: ["Process automation", "Digital workflows", "Change management", "Technology consulting"]
        }
      },
      serviceCategories: {
        development: {
          title: "Development Services",
          description: "Custom software solutions built with cutting-edge technologies",
          services: [
            "Web Application Development",
            "Mobile App Development",
            "API Development & Integration",
            "E-commerce Solutions",
            "Content Management Systems",
            "Progressive Web Applications"
          ]
        },
        cloud: {
          title: "Cloud Services",
          description: "Scalable cloud infrastructure and migration services",
          services: [
            "Cloud Migration",
            "Infrastructure as Code",
            "Container Orchestration",
            "Serverless Architecture",
            "Cloud Security",
            "Performance Optimization"
          ]
        },
        consulting: {
          title: "Technology Consulting",
          description: "Strategic guidance for your digital transformation journey",
          services: [
            "Digital Strategy Planning",
            "Technology Assessment",
            "Architecture Design",
            "Process Optimization",
            "Team Training",
            "Project Management"
          ]
        }
      },
      faqs: [
        {
          question: "What technologies do you specialize in?",
          answer: "We work with modern technologies including React, Next.js, Node.js, Python, AWS, Azure, Docker, Kubernetes, and many more. Our team stays current with the latest industry trends and best practices."
        },
        {
          question: "How do you ensure project quality?",
          answer: "We follow rigorous testing procedures, code reviews, and agile development methodologies. Every project includes comprehensive testing, documentation, and ongoing support to ensure the highest quality deliverables."
        },
        {
          question: "What is your typical project timeline?",
          answer: "Project timelines vary based on complexity and scope. Simple projects may take 2-4 weeks, while complex enterprise solutions can take 3-6 months. We provide detailed timelines during the planning phase."
        },
        {
          question: "Do you provide ongoing support?",
          answer: "Yes, we offer comprehensive support packages including maintenance, updates, monitoring, and technical support. Our team is available 24/7 for critical issues and provides regular maintenance windows for updates."
        },
        {
          question: "Can you work with our existing systems?",
          answer: "Absolutely! We specialize in integrating with existing systems and can help modernize legacy applications. Our team has experience with various platforms and can ensure seamless integration."
        }
      ]
    },

    // About Page
    about: {
      title: "About Agnus Cloud",
      subtitle: "We're a creative technology company that believes in the power of innovation to transform businesses. Like a lamb in the clouds, we combine gentleness with strength, creativity with technical excellence.",
      workWithUs: "Work With Us",
      ourServices: "Our Services",
      ourMission: "Our Mission",
      missionText: "To empower businesses with innovative technology solutions that drive growth, efficiency, and success. We believe that technology should be accessible, reliable, and transformative for organizations of all sizes.",
      ourVision: "Our Vision",
      visionText: "To be the leading creative technology partner that businesses trust for their digital transformation journey. We envision a future where technology seamlessly integrates with human creativity to solve complex challenges.",
      ourValues: "Our Values",
      valuesSubtitle: "The principles that guide everything we do and shape our company culture.",
      ourJourney: "Our Journey",
      journeySubtitle: "From humble beginnings to industry recognition, here's our story.",
      meetOurTeam: "Meet Our Team",
      teamSubtitle: "The talented individuals who make Agnus Cloud's vision a reality.",
      readyToJoin: "Ready to Join Our Journey?",
      readyToJoinSubtitle: "Whether you're looking for a technology partner or want to join our team, we'd love to hear from you.",
      startAProject: "Start a Project",
      viewOurWork: "View Our Work",
      values: {
        passionDriven: {
          title: "Passion-Driven",
          description: "We're passionate about technology and its power to transform businesses and lives."
        },
        innovationFirst: {
          title: "Innovation First",
          description: "We constantly explore new technologies and creative solutions to stay ahead of the curve."
        },
        clientCentric: {
          title: "Client-Centric",
          description: "Your success is our success. We build lasting partnerships based on trust and results."
        },
        excellence: {
          title: "Excellence",
          description: "We maintain the highest standards in everything we do, from code quality to customer service."
        }
      },
      timeline: [
        {
          year: "2020",
          title: "The Beginning",
          description: "Agnus Cloud was founded with a vision to bridge the gap between creative design and robust technology."
        },
        {
          year: "2021",
          title: "First Major Client",
          description: "Successfully delivered our first enterprise-level cloud migration project, establishing our reputation for excellence."
        },
        {
          year: "2022",
          title: "Team Expansion",
          description: "Grew our team to include specialists in AI, machine learning, and advanced cloud architectures."
        },
        {
          year: "2023",
          title: "Innovation Lab",
          description: "Launched our innovation lab to research emerging technologies and develop cutting-edge solutions."
        },
        {
          year: "2024",
          title: "Global Reach",
          description: "Expanded our services globally, helping businesses worldwide transform their digital presence."
        }
      ],
      team: [
        {
          name: "Alex Chen",
          role: "Founder & CEO",
          description: "Visionary leader with 15+ years in tech innovation and business strategy."
        },
        {
          name: "Sarah Johnson",
          role: "CTO",
          description: "Technical architect specializing in cloud infrastructure and scalable systems."
        },
        {
          name: "Marcus Rodriguez",
          role: "Head of Design",
          description: "Creative director with expertise in UX/UI and digital product design."
        },
        {
          name: "Emily Zhang",
          role: "Lead Developer",
          description: "Full-stack engineer passionate about clean code and modern frameworks."
        }
      ]
    },

    // Footer
    footer: {
      description: "",
      navigation: "Navigation",
      legal: "Legal",
      socialMedia: "Social Media",
      subsidiaryText: "Subsidiary of AG. Enterprise",
      noVisitingAddress: "(No visiting address)",
      links: {
        home: "Home",
        about: "About Us",
        contact: "Book an Appointment",
        portfolio: "Portfolio",
        terms: "Terms and Conditions",
        returns: "Return Policy",
        privacy: "Privacy Policy"
      }
    },

    // Contact Page
    contact: {
      title: "Get in Touch",
      subtitle: "Ready to transform your ideas into reality? Let's start a conversation about your next project and explore how we can help you succeed.",
      emailUs: "Email Us",
      emailDescription: "Send us an email anytime",
      callUs: "Call Us",
      callDescription: "Mon-Fri 9AM-6PM CET",
      visitUs: "Visit Us",
      visitDescription: "Our headquarters",
      noVisitingAddress: "(No visiting address)",
      sendMessage: "Send Us a Message",
      sendMessageSubtitle: "Fill out the form below and we'll get back to you as soon as possible.",
      contactForm: "Contact Form",
      fullName: "Full Name",
      emailAddress: "Email Address",
      subject: "Subject",
      message: "Message",
      messagePlaceholder: "Tell us about your project or inquiry...",
      consent: "I agree to the privacy policy and terms of service",
      sendingMessage: "Sending Message...",
      sendMessageButton: "Send Message",
      quickResponse: "Quick Response Guarantee",
      quickResponseText: "We typically respond to all inquiries within 24 hours during business days.",
      urgentMatters: "For urgent matters, please call us directly at +31 85 - 488 354 3",
      subjects: [
        "General Inquiry",
        "Project Consultation",
        "Technical Support",
        "Partnership Opportunity",
        "Career Inquiry",
        "Other"
      ],
      validation: {
        nameRequired: "Name must be at least 2 characters",
        emailRequired: "Please enter a valid email address",
        subjectRequired: "Please select a subject",
        messageRequired: "Message must be at least 10 characters",
        consentRequired: "You must agree to the privacy policy"
      },
      success: {
        title: "Message sent successfully!",
        description: "We'll get back to you within 24 hours."
      },
      error: {
        title: "Something went wrong",
        description: "Please try again or contact us directly."
      },
      booking: {
        planYourCall: "Plan your call",
        freeConsultation: "Free consultation meeting",
        personalizedAdvice: "Personalized advice tailored to you",
        onlineOrOnsite: "Online or on-site",
        stepOf: "Step {current} of {total}"
      }
    },

    // Loading Screen
    loading: {
      tagline: "Creative Tech Solutions",
      preparing: "Preparing your experience..."
    },

    // 404 Page
    notFound: {
      title: "Page not found",
      description: "This page doesn't exist or has been moved.",
      backHome: "Back to home"
    }
  },

  nl: {
    // Navigatie
    nav: {
      home: "Home",
      features: "Portfolio",
      about: "Over ons",
      contact: "Boek een afspraak"
    },

    // Home Pagina
    home: {
      title: "Jouw Digitale Dromen, Echt Gemaakt",
      heroTitle: "Van Idee Naar Impact, Samen Sterker",
      subtitle: "Bij Agnus Cloud bouwen we digitale oplossingen die jouw missie versterken. Vanuit vertrouwen, vakmanschap en visie.",
      exploreTools: "Bekijk Wat Wij Doen",
      learnMore: "Leer Ons Kennen",
      whyChoose: "Waarom Kiezen voor Agnus Cloud?",
      whyChooseSubtitle: "Wij zijn niet zomaar een tech bedrijf. Wij zijn het team dat enthousiast wordt van jouw succes en jouw project behandelt alsof het ons eigen is.",
      trustedBy: "Vertrouwd door Geweldige Bedrijven",
      trustedBySubtitle: "Echte mensen, echte resultaten, echte relaties",
      readyTransform: "Klaar om Iets Geweldigs te Maken?",
      readyTransformSubtitle: "Laten we een koffie pakken (virtueel of echt) en praten over jouw dromen. Wij maken graag van 'wat als' naar 'wauw, dat is ongelooflijk!'",
      startProject: "Laten We Samen Bouwen",
      stats: {
        projects: "Projecten Opgeleverd",
        uptime: "Uptime Garantie",
        support: "Ondersteuning Beschikbaar",
        clients: "Tevreden Klanten"
      },
      features: {
        cloudNative: {
          title: "Cloud-Native Oplossingen",
          description: "Schaalbare, moderne applicaties gebouwd voor de cloud-first wereld."
        },
        lightningFast: {
          title: "Bliksemsnelle Prestaties",
          description: "Geoptimaliseerde prestaties die resultaten leveren met de snelheid van het denken."
        },
        enterpriseSecurity: {
          title: "Enterprise Beveiliging",
          description: "Bankwaardige beveiligingsprotocollen die uw meest waardevolle data beschermen."
        },
        creativeInnovation: {
          title: "Creatieve Innovatie",
          description: "Geavanceerde technologie ontmoet creatieve probleemoplossing."
        }
      }
    },

    // Partners Sectie
    partners: {
      title: "De Geweldige Tools Waarmee Wij Werken",
      subtitle: "Wij hebben samengewerkt met de beste techbedrijven om u oplossingen te brengen die gewoon werken. Elke tool die wij kiezen helpt ons iets ongelooflijks voor uw bedrijf te bouwen.",
      techStack: "Onze Technologie Gereedschapskist",
      techStackDescription: "Wij hebben elke tool in onze gereedschapskist zorgvuldig gekozen omdat ze ons helpen iets geweldigs voor u te bouwen. Of het nu gaat om uw code veilig houden met GitHub, uw website bliksemsnell maken met Vercel, of ervoor zorgen dat alles soepel loopt met UptimeRobot, elke keuze die wij maken draait om het leveren van de beste ervaring voor uw bedrijf.",
      details: {
        github: {
          title: "GitHub",
          description: "Versiebeheer & Samenwerking",
          integration: "Wij gebruiken GitHub om onze codebase te beheren, efficiënt samen te werken en versiebeheer voor al onze projecten te waarborgen. Het maakt transparant teamwork en code reviews mogelijk."
        },
        nextjs: {
          title: "Next.js",
          description: "React Framework",
          integration: "Next.js vormt de basis van onze frontend met server-side rendering, statische generatie en API-routes. Hiermee bouwen we snelle, SEO-vriendelijke en schaalbare webapplicaties."
        },
        vercel: {
          title: "Vercel",
          description: "Deploy Platform",
          integration: "Wij deployen onze applicaties op Vercel voor directe, wereldwijde levering. Het edge-netwerk van Vercel zorgt voor snelle en betrouwbare sites, met zero-config deploys en naadloze Next.js-integratie."
        },
        cloudflare: {
          title: "Cloudflare",
          description: "CDN & Beveiliging",
          integration: "Cloudflare beschermt onze sites tegen bedreigingen en versnelt wereldwijde contentlevering. We gebruiken het voor DDoS-bescherming, SSL en caching, voor optimale veiligheid en prestaties."
        },
        uptimerobot: {
          title: "UptimeRobot",
          description: "Monitoring & Alerts",
          integration: "UptimeRobot monitort onze diensten 24/7 en waarschuwt ons direct bij storingen. Zo houden we hoge beschikbaarheid en kunnen we snel reageren op problemen."
        },
        supabase: {
          title: "Supabase",
          description: "Backend as a Service",
          integration: "Supabase levert onze backend-infrastructuur, inclusief authenticatie, database en real-time API's. Zo bouwen we veilige, schaalbare apps zonder serverbeheer."
        },
        mijndomein: {
          title: "MijnDomein.nl",
          description: "Domeinbeheer",
          integration: "Wij beheren onze domeinen, DNS en SSL-certificaten via MijnDomein.nl. Zo zijn onze webdomeinen altijd bereikbaar en veilig."
        }
      }
    },

    // Portfolio Pagina
    features: {
      title: "Ons Portfolio",
      subtitle: "Ontdek het uitgebreide pakket van diensten en technologieën die Agnus Cloud de perfecte partner maken voor uw digitale transformatiereis.",
      serviceCategoriesTitle: "Service Categorieën",
      serviceCategoriesSubtitle: "Verken ons uitgebreide aanbod van diensten ontworpen om aan al uw technologiebehoeften te voldoen.",
      faq: "Veelgestelde Vragen",
      faqSubtitle: "Krijg antwoorden op veelgestelde vragen over onze diensten en processen.",
      readyToStart: "Klaar om te Beginnen?",
      readyToStartSubtitle: "Laten we bespreken hoe onze functies uw bedrijf kunnen helpen transformeren. Neem vandaag nog contact op voor een gratis consultatie.",
      getStartedToday: "Begin Vandaag",
      learnAboutUs: "Meer Over Ons",
      mainFeatures: {
        cloudInfrastructure: {
          title: "Cloud Infrastructuur",
          description: "Schaalbare, betrouwbare cloud-oplossingen gebouwd voor moderne bedrijven.",
          features: ["Auto-scaling", "99.9% uptime", "Wereldwijde CDN", "Disaster recovery"]
        },
        customDevelopment: {
          title: "Maatwerk Ontwikkeling",
          description: "Op maat gemaakte softwareoplossingen die passen bij uw unieke bedrijfsbehoeften.",
          features: ["Full-stack ontwikkeling", "API integratie", "Legacy modernisering", "Code optimalisatie"]
        },
        mobileSolutions: {
          title: "Mobiele Oplossingen",
          description: "Native en cross-platform mobiele apps die gebruikers betrekken.",
          features: ["iOS & Android", "React Native", "Progressive Web Apps", "App Store optimalisatie"]
        },
        dataAnalytics: {
          title: "Data Analytics",
          description: "Transformeer uw data naar bruikbare bedrijfsinzichten.",
          features: ["Real-time dashboards", "Voorspellende analytics", "Data visualisatie", "Machine learning"]
        },
        cybersecurity: {
          title: "Cyberbeveiliging",
          description: "Uitgebreide beveiligingsoplossingen om uw digitale assets te beschermen.",
          features: ["Dreigingsdetectie", "Compliance auditing", "Beveiligingstraining", "Incident response"]
        },
        digitalTransformation: {
          title: "Digitale Transformatie",
          description: "End-to-end digitale transformatie voor moderne ondernemingen.",
          features: ["Procesautomatisering", "Digitale workflows", "Change management", "Technologie consulting"]
        }
      },
      serviceCategories: {
        development: {
          title: "Ontwikkelingsdiensten",
          description: "Maatwerk softwareoplossingen gebouwd met geavanceerde technologieën",
          services: [
            "Webapplicatie Ontwikkeling",
            "Mobiele App Ontwikkeling",
            "API Ontwikkeling & Integratie",
            "E-commerce Oplossingen",
            "Content Management Systemen",
            "Progressive Web Applicaties"
          ]
        },
        cloud: {
          title: "Cloud Diensten",
          description: "Schaalbare cloud infrastructuur en migratiediensten",
          services: [
            "Cloud Migratie",
            "Infrastructure as Code",
            "Container Orchestratie",
            "Serverless Architectuur",
            "Cloud Beveiliging",
            "Prestatie Optimalisatie"
          ]
        },
        consulting: {
          title: "Technologie Consulting",
          description: "Strategische begeleiding voor uw digitale transformatiereis",
          services: [
            "Digitale Strategie Planning",
            "Technologie Assessment",
            "Architectuur Ontwerp",
            "Proces Optimalisatie",
            "Team Training",
            "Project Management"
          ]
        }
      },
      faqs: [
        {
          question: "In welke technologieën zijn jullie gespecialiseerd?",
          answer: "Wij werken met moderne technologieën waaronder React, Next.js, Node.js, Python, AWS, Azure, Docker, Kubernetes, en vele anderen. Ons team blijft op de hoogte van de nieuwste industrietrends en best practices."
        },
        {
          question: "Hoe zorgen jullie voor projectkwaliteit?",
          answer: "Wij volgen rigoureuze testprocedures, code reviews, en agile ontwikkelingsmethodologieën. Elk project bevat uitgebreide testing, documentatie, en doorlopende ondersteuning om de hoogste kwaliteit te garanderen."
        },
        {
          question: "Wat is jullie typische projecttijdlijn?",
          answer: "Projecttijdlijnen variëren op basis van complexiteit en scope. Eenvoudige projecten kunnen 2-4 weken duren, terwijl complexe enterprise oplossingen 3-6 maanden kunnen duren. Wij verstrekken gedetailleerde tijdlijnen tijdens de planningsfase."
        },
        {
          question: "Bieden jullie doorlopende ondersteuning?",
          answer: "Ja, wij bieden uitgebreide ondersteuningspakketten inclusief onderhoud, updates, monitoring, en technische ondersteuning. Ons team is 24/7 beschikbaar voor kritieke problemen en biedt regelmatige onderhoudsvensters voor updates."
        },
        {
          question: "Kunnen jullie werken met onze bestaande systemen?",
          answer: "Absoluut! Wij zijn gespecialiseerd in integratie met bestaande systemen en kunnen helpen bij het moderniseren van legacy applicaties. Ons team heeft ervaring met verschillende platforms en kan naadloze integratie garanderen."
        }
      ]
    },

    // Over Ons Pagina
    about: {
      title: "Over Agnus Cloud",
      subtitle: "Wij zijn een creatief technologiebedrijf dat gelooft in de kracht van innovatie om bedrijven te transformeren. Zoals een lam in de wolken, combineren wij zachtheid met kracht, creativiteit met technische excellentie.",
      workWithUs: "Werk Met Ons",
      ourServices: "Onze Diensten",
      ourMission: "Onze Missie",
      missionText: "Bedrijven empoweren met innovatieve technologieoplossingen die groei, efficiëntie en succes stimuleren. Wij geloven dat technologie toegankelijk, betrouwbaar en transformatief moet zijn voor organisaties van alle groottes.",
      ourVision: "Onze Visie",
      visionText: "De leidende creatieve technologiepartner zijn die bedrijven vertrouwen voor hun digitale transformatiereis. Wij stellen ons een toekomst voor waarin technologie naadloos integreert met menselijke creativiteit om complexe uitdagingen op te lossen.",
      ourValues: "Onze Waarden",
      valuesSubtitle: "De principes die alles wat wij doen leiden en onze bedrijfscultuur vormgeven.",
      ourJourney: "Onze Reis",
      journeySubtitle: "Van bescheiden begin tot industriële erkenning, hier is ons verhaal.",
      meetOurTeam: "Ontmoet Ons Team",
      teamSubtitle: "De getalenteerde individuen die Agnus Cloud's visie werkelijkheid maken.",
      readyToJoin: "Klaar om Deel te Nemen aan Onze Reis?",
      readyToJoinSubtitle: "Of u nu op zoek bent naar een technologiepartner of deel wilt uitmaken van ons team, wij horen graag van u.",
      startAProject: "Start een Project",
      viewOurWork: "Bekijk Ons Werk",
      values: {
        passionDriven: {
          title: "Passie-Gedreven",
          description: "Wij zijn gepassioneerd over technologie en de kracht ervan om bedrijven en levens te transformeren."
        },
        innovationFirst: {
          title: "Innovatie Eerst",
          description: "Wij verkennen voortdurend nieuwe technologieën en creatieve oplossingen om voorop te blijven lopen."
        },
        clientCentric: {
          title: "Klant-Centrisch",
          description: "Uw succes is ons succes. Wij bouwen duurzame partnerschappen gebaseerd op vertrouwen en resultaten."
        },
        excellence: {
          title: "Excellentie",
          description: "Wij handhaven de hoogste standaarden in alles wat wij doen, van codekwaliteit tot klantenservice."
        }
      },
      timeline: [
        {
          year: "2020",
          title: "Het Begin",
          description: "Agnus Cloud werd opgericht met een visie om de kloof tussen creatief ontwerp en robuuste technologie te overbruggen."
        },
        {
          year: "2021",
          title: "Eerste Grote Klant",
          description: "Succesvol onze eerste enterprise-level cloud migratieproject opgeleverd, waarmee onze reputatie voor excellentie werd gevestigd."
        },
        {
          year: "2022",
          title: "Team Uitbreiding",
          description: "Ons team uitgebreid met specialisten in AI, machine learning, en geavanceerde cloud architecturen."
        },
        {
          year: "2023",
          title: "Innovatie Lab",
          description: "Ons innovatielab gelanceerd om opkomende technologieën te onderzoeken en geavanceerde oplossingen te ontwikkelen."
        },
        {
          year: "2024",
          title: "Wereldwijd Bereik",
          description: "Onze diensten wereldwijd uitgebreid, waarbij we bedrijven wereldwijd helpen hun digitale aanwezigheid te transformeren."
        }
      ],
      team: [
        {
          name: "Alex Chen",
          role: "Oprichter & CEO",
          description: "Visionaire leider met 15+ jaar ervaring in tech innovatie en bedrijfsstrategie."
        },
        {
          name: "Sarah Johnson",
          role: "CTO",
          description: "Technische architect gespecialiseerd in cloud infrastructuur en schaalbare systemen."
        },
        {
          name: "Marcus Rodriguez",
          role: "Hoofd Ontwerp",
          description: "Creatieve directeur met expertise in UX/UI en digitaal productontwerp."
        },
        {
          name: "Emily Zhang",
          role: "Lead Developer",
          description: "Full-stack engineer gepassioneerd over schone code en moderne frameworks."
        }
      ]
    },

    // Contact Pagina
    contact: {
      title: "Neem Contact Op",
      subtitle: "Klaar om uw ideeën om te zetten in werkelijkheid? Laten we een gesprek starten over uw volgende project en verkennen hoe wij u kunnen helpen slagen.",
      emailUs: "E-mail Ons",
      emailDescription: "Stuur ons altijd een e-mail",
      callUs: "Bel Ons",
      callDescription: "Ma-Vr 9:00-18:00 CET",
      visitUs: "Bezoek Ons",
      visitDescription: "Ons hoofdkantoor",
      noVisitingAddress: "(Geen bezoekbaar adres)",
      sendMessage: "Stuur Ons een Bericht",
      sendMessageSubtitle: "Vul het onderstaande formulier in en wij nemen zo snel mogelijk contact met u op.",
      contactForm: "Contact Formulier",
      fullName: "Volledige Naam",
      emailAddress: "E-mailadres",
      subject: "Onderwerp",
      message: "Bericht",
      messagePlaceholder: "Vertel ons over uw project of vraag...",
      consent: "Ik ga akkoord met het privacybeleid en de algemene voorwaarden",
      sendingMessage: "Bericht Verzenden...",
      sendMessageButton: "Verstuur Bericht",
      quickResponse: "Snelle Reactie Garantie",
      quickResponseText: "Wij reageren doorgaans binnen 24 uur op alle vragen tijdens werkdagen.",
      urgentMatters: "Voor urgente zaken kunt u ons direct bellen op +31 85 - 488 354 3",
      subjects: [
        "Algemene Vraag",
        "Project Consultatie",
        "Technische Ondersteuning",
        "Partnership Mogelijkheid",
        "Carrière Vraag",
        "Anders"
      ],
      validation: {
        nameRequired: "Naam moet minimaal 2 karakters bevatten",
        emailRequired: "Voer een geldig e-mailadres in",
        subjectRequired: "Selecteer een onderwerp",
        messageRequired: "Bericht moet minimaal 10 karakters bevatten",
        consentRequired: "U moet akkoord gaan met het privacybeleid"
      },
      success: {
        title: "Bericht succesvol verzonden!",
        description: "Wij nemen binnen 24 uur contact met u op."
      },
      error: {
        title: "Er is een fout opgetreden",
        description: "Probeer het opnieuw of neem contact met ons op."
      },
      booking: {
        planYourCall: "Plan je gesprek",
        freeConsultation: "Gratis kennismakingsgesprek",
        personalizedAdvice: "Persoonlijk advies op maat",
        onlineOrOnsite: "Online of op locatie",
        stepOf: "Stap {current} van {total}"
      }
    },

    // Footer
    footer: {
      description: "",
      navigation: "Navigatie",
      legal: "Juridisch",
      socialMedia: "Social Media",
      subsidiaryText: "Dochter onderneming van AG. Enterprise",
      noVisitingAddress: "(Geen bezoekbaar adres)",
      links: {
        home: "Home",
        about: "Over ons",
        contact: "Boek een afspraak",
        portfolio: "Portfolio",
        terms: "Algemene voorwaarden",
        returns: "Restitutiebeleid",
        privacy: "Privacy beleid"
      }
    },

    // Laadscherm
    loading: {
      tagline: "Creatieve Tech Oplossingen",
      preparing: "Uw ervaring voorbereiden..."
    },

    // 404-pagina
    notFound: {
      title: "Pagina niet gevonden",
      description: "Deze pagina bestaat niet (meer) of is verplaatst.",
      backHome: "Terug naar home"
    }
  }
};

export type Language = keyof typeof translations;
export type TranslationKey = keyof typeof translations.en;